import * as React from 'react'
import * as t from 'io-ts'
import { fold } from 'fp-ts/lib/Either'
import * as Utils from './Utils'
import mockData from './mock.json'

export function useApi<T>(url: string, codec: t.Type<T>) {
  const [isLoading, setLoading] = React.useState<boolean>(true)
  const [error, setError] = React.useState<Error | null>(null)
  const [data, setData] = React.useState<T | null>(null)

  const revalidate = React.useCallback(async () => {
    setLoading(true)
    setError(null)

    try {
      const response = await request(url)
      const decodedResponse = await decode(response, JSendResponse(codec))

      switch (decodedResponse.status) {
        case 'error':
          setError(new Error('Unknown error happened'))
          break
        case 'fail':
          setError(new Error(decodedResponse.message))
          break
        case 'success':
          setData(decodedResponse.data)
          break
        default:
          Utils.assertNever(decodedResponse)
      }
    } catch (e) {
      setError(e)
    }

    setLoading(false)
  }, [url, codec])

  React.useEffect(() => {
    revalidate()
  }, [revalidate])

  return { data, isLoading, error, revalidate }
}

const JSendResponse = <T>(codec: t.Type<T>) =>
  t.union([
    t.type({
      status: t.literal('success'),
      data: codec,
    }),
    t.type({
      status: t.literal('fail'),
      message: t.string,
    }),
    t.type({
      status: t.literal('error'),
    }),
  ])

function decode<T>(data: any, codec: t.Type<T>): Promise<T> {
  const result = codec.decode(data)
  return fold<t.Errors, T, Promise<T>>(
    ([error]) => {
      const actualErr = error.context[error.context.length - 1]
      const errPath = error.context
        .map((c) => c.key)
        .filter((key) => key.length > 0)

      const typeOf = (typedValue: any) =>
        Array.isArray(typedValue) ? 'array' : typeof typedValue

      const niceError = new Error(
        `${['[VALUE]', ...errPath].join('.')} expected ${
          actualErr.type.name
        }, but got ${typeOf(error.value)} (${
          error.value === undefined ? 'undefined' : JSON.stringify(error.value)
        })`
      )

      return Promise.reject(niceError)
    },
    (decodedValue) => Promise.resolve(decodedValue)
  )(result)
}

// JUST MOCK STUFF
const loadMockData = () => JSON.parse(JSON.stringify(mockData))
const request = async (url: string) => {
  await new Promise((resolve) => setTimeout(resolve, 300))

  return {
    status: 'success',
    message: null,
    data: {
      id: 1,
      type: 'classifier',
      name: 1 + ' Hello world!',
      children: loadMockData(),
    },
  }
}
